import { useFeedbackSettingsStore, FeedbackSoundCategory } from '../store/feedbackSettingsStore'

type SoundTone = {
  frequency: number
  duration: number
  type?: OscillatorType
  gain?: number
  delay?: number
}

let audioContext: AudioContext | null = null
let unlocked = false

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (!audioContext) {
    const Ctx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return null
    audioContext = new Ctx()
  }
  return audioContext
}

export function unlockFeedbackAudio(): void {
  const ctx = getAudioContext()
  if (!ctx || unlocked) return
  if (ctx.state === 'suspended') {
    void ctx.resume()
  }
  unlocked = true
}

function playTone(ctx: AudioContext, tone: SoundTone, masterGain: number) {
  const oscillator = ctx.createOscillator()
  const gainNode = ctx.createGain()
  const startAt = ctx.currentTime + (tone.delay ?? 0)
  const duration = Math.max(0.04, tone.duration)

  oscillator.type = tone.type ?? 'sine'
  oscillator.frequency.setValueAtTime(tone.frequency, startAt)

  const peak = Math.min(1, Math.max(0.01, (tone.gain ?? 0.12) * masterGain))
  gainNode.gain.setValueAtTime(0.0001, startAt)
  gainNode.gain.exponentialRampToValueAtTime(peak, startAt + 0.01)
  gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)

  oscillator.connect(gainNode)
  gainNode.connect(ctx.destination)
  oscillator.start(startAt)
  oscillator.stop(startAt + duration + 0.02)
}

const SOUND_PATTERNS: Record<FeedbackSoundCategory, SoundTone[]> = {
  success: [
    { frequency: 523, duration: 0.09, gain: 0.14 },
    { frequency: 659, duration: 0.12, gain: 0.16, delay: 0.08 },
  ],
  save: [
    { frequency: 440, duration: 0.08, gain: 0.13 },
    { frequency: 554, duration: 0.1, gain: 0.15, delay: 0.07 },
    { frequency: 659, duration: 0.14, gain: 0.17, delay: 0.15 },
  ],
  error: [
    { frequency: 220, duration: 0.16, type: 'square', gain: 0.1 },
    { frequency: 185, duration: 0.2, type: 'square', gain: 0.1, delay: 0.12 },
  ],
  warning: [
    { frequency: 740, duration: 0.1, gain: 0.13 },
    { frequency: 620, duration: 0.12, gain: 0.13, delay: 0.14 },
  ],
  info: [
    { frequency: 600, duration: 0.1, gain: 0.11 },
  ],
  notification: [
    { frequency: 880, duration: 0.08, gain: 0.12 },
    { frequency: 988, duration: 0.1, gain: 0.13, delay: 0.09 },
    { frequency: 1175, duration: 0.12, gain: 0.14, delay: 0.18 },
  ],
  fuelStation: [
    { frequency: 392, duration: 0.08, gain: 0.13 },
    { frequency: 523, duration: 0.1, gain: 0.14, delay: 0.08 },
    { frequency: 659, duration: 0.12, gain: 0.15, delay: 0.16 },
  ],
}

export function playFeedbackSound(category: FeedbackSoundCategory): void {
  const { enabled, volume, categories } = useFeedbackSettingsStore.getState()
  if (!enabled || !categories[category]) return

  const ctx = getAudioContext()
  if (!ctx) return
  if (ctx.state === 'suspended') {
    void ctx.resume()
  }

  const pattern = SOUND_PATTERNS[category]
  if (!pattern) return
  pattern.forEach((tone) => playTone(ctx, tone, volume))
}

export function previewFeedbackSound(category: FeedbackSoundCategory): void {
  unlockFeedbackAudio()
  const previous = useFeedbackSettingsStore.getState().enabled
  useFeedbackSettingsStore.setState({ enabled: true })
  playFeedbackSound(category)
  useFeedbackSettingsStore.setState({ enabled: previous })
}
